import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {CEREMONY_2FA_SMS_PATH, CEREMONY_2FA_QNA_PATH, SSO_VERIFIED_PARAM, ACCOUNT_FEATURE_KEYS} from '@constants';
import type {TwoFactorAuthenticationResource} from '@api/recipient/api.d';
import {selectAccessToken} from '@components/Login/userSlice';
import {getIsSender} from '@utils/userRole';
import {selectRecipient, selectTransactionId, selectAccountFeatures} from '../signerUiSlice';
import {getIsAccountFeatureActive} from '../utils/getIsAccountFeatureActive';
import {getIs2FACompleted} from '../2FA/utils';

const TWO_FA_TYPE_SMS = 'sms';
const TWO_FA_TYPE_QA = 'qa';
const TWO_FA_TYPE_SSO = 'sso';

const SSO_LOGIN_BASE_PATH = '/sso/saml/login/alias';

const TWO_FA_TYPE_TO_PATH: Record<string, string> = {
    [TWO_FA_TYPE_SMS]: CEREMONY_2FA_SMS_PATH,
    [TWO_FA_TYPE_QA]: CEREMONY_2FA_QNA_PATH
};

interface TwoFARedirectResult {
    is2FARequired: boolean;
    isSSORedirectInProgress: boolean;
    redirectPath: string | null;
}

const TWO_FA_TYPE_NONE = 'none';

function getIs2FARequired(twoFactorAuth: TwoFactorAuthenticationResource | null | undefined): boolean {
    if (!twoFactorAuth || twoFactorAuth.type === TWO_FA_TYPE_NONE) {
        return false;
    }

    return !twoFactorAuth.locked;
}

function getSSORedirectUrl(twoFactorAuth: TwoFactorAuthenticationResource, transactionId: string): string | null {
    const alias = twoFactorAuth.challenges?.[0]?.alias;

    if (!alias) {
        return null;
    }

    return `${SSO_LOGIN_BASE_PATH}/${alias}?transaction=${transactionId}`;
}

function get2FARedirectPath(twoFactorAuth: TwoFactorAuthenticationResource | null | undefined): string | null {
    if (!twoFactorAuth) {
        return null;
    }

    return TWO_FA_TYPE_TO_PATH[twoFactorAuth.type] ?? null;
}

function getIsAlreadyOn2FAPage(pathname: string): boolean {
    return pathname.includes(CEREMONY_2FA_SMS_PATH) || pathname.includes(CEREMONY_2FA_QNA_PATH);
}

/** Hook that handles 2FA redirect logic during the signing ceremony. */
export function useTwoFARedirect(): TwoFARedirectResult {
    const navigate = useNavigate();
    const location = useLocation();
    const recipient = useSelector(selectRecipient);
    const transactionId = useSelector(selectTransactionId);
    const accessToken = useSelector(selectAccessToken);
    const accountFeatures = useSelector(selectAccountFeatures);
    const isSender = getIsSender(accessToken);
    const recipientId = recipient?.id ?? null;
    const twoFactorAuth = recipient?.two_factor_authentication;
    const is2FACompleted = getIs2FACompleted(transactionId, recipientId);
    const isAlreadyOn2FAPage = getIsAlreadyOn2FAPage(location.pathname);
    const isSsoJustVerified = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get(SSO_VERIFIED_PARAM) === 'true';
    }, [location.search]);
    const isAlwaysRequireTwoFactorEnabled = getIsAccountFeatureActive(
        accountFeatures,
        ACCOUNT_FEATURE_KEYS.ALWAYS_REQUIRE_TWO_FACTOR_WHEN_PROVIDED
    );
    const shouldSenderSkipTwoFA = isSender && !isAlwaysRequireTwoFactorEnabled;
    const is2FARequired = !shouldSenderSkipTwoFA && !is2FACompleted && !isSsoJustVerified && !isAlreadyOn2FAPage && getIs2FARequired(twoFactorAuth);
    const redirectPath = get2FARedirectPath(twoFactorAuth);
    const isSSOType = twoFactorAuth?.type === TWO_FA_TYPE_SSO;



    useEffect(() => {
        if (!is2FARequired) {
            return;
        }

        if (isSSOType && transactionId && twoFactorAuth) {
            const ssoRedirectUrl = getSSORedirectUrl(twoFactorAuth, transactionId);

            if (ssoRedirectUrl) {
                window.location.href = ssoRedirectUrl;
            }

            return;
        }

        if (!redirectPath) {
            return;
        }

        navigate(redirectPath, {replace: true});
    }, [is2FARequired, isSSOType, redirectPath, navigate, transactionId, twoFactorAuth]);

    const isSSORedirectInProgress = is2FARequired && isSSOType;

    return {
        is2FARequired,
        isSSORedirectInProgress,
        redirectPath
    };
}

